/**
 * buildPseudonymizationContext — leitet den Pseudonymisierungs-Kontext aus
 * Case-Stammdaten + ClientStaticData ab.
 *
 * Vor jedem Mistral-Call MUSS pseudonymize() mit diesem Kontext aufgerufen
 * werden, sonst landen Klarnamen/Adressen im LLM. Der mistralClient hat
 * zwar einen PII-Pattern-Re-Check als defense-in-depth, aber wenn der
 * greift, ist es ein Bug — die Pseudonymisierung gehört in den UI-Pfad.
 *
 * Pure function — keine IO, keine React.
 */

import type { statement } from '@tragfaehigkeit/core';
import type { CaseRow } from '../supabaseTypes';
import type { PseudonymizationContext } from './pseudonymize';

type ClientStaticData = statement.ClientStaticData;

// Re-export für sauberen Import in Aufrufern (Hook, Button-Komponente).
export type { PseudonymizationContext };

/**
 * Baut den Pseudonymisierungs-Kontext aus einer Case-Row + ClientStaticData.
 *
 * Im Live-Pfad: client kommt aus buildInputFromCase, jcName aus case.jobcenter_name.
 * Im Demo-Pfad: client + caseData kommen aus buildDemoStatementInput.
 *
 * Robust gegen NULL-Felder — minimaler clientFullName-Default verhindert
 * dass der Pseudonymizer auf undefined trifft.
 */
export function buildPseudonymizationContextFromCase(args: {
  readonly client: ClientStaticData;
  readonly caseRow: Pick<CaseRow, 'jc_file_number' | 'jobcenter_name'> | null;
  readonly currentDate?: Date;
}): PseudonymizationContext {
  const { client, caseRow, currentDate } = args;
  const ctx: PseudonymizationContext = {
    clientFullName: client.fullName,
    ...(client.firstName !== undefined ? { clientFirstName: client.firstName } : {}),
    ...(client.address !== undefined ? { clientAddress: client.address } : {}),
    ...(client.birthDate !== undefined ? { clientBirthDate: client.birthDate } : {}),
    ...(caseRow?.jc_file_number ? { jcFileNumber: caseRow.jc_file_number } : {}),
    ...(caseRow?.jobcenter_name ? { jcName: caseRow.jobcenter_name } : {}),
    ...(currentDate !== undefined ? { currentDate } : {}),
  };
  return ctx;
}

/**
 * Minimal-Variante für Smoke-Tests oder Cockpit-Demo-Modus ohne CaseRow.
 * Nimmt nur ClientStaticData und verzichtet auf Jobcenter-Tokens.
 */
export function buildPseudonymizationContextFromClient(
  client: ClientStaticData,
  currentDate?: Date,
): PseudonymizationContext {
  return buildPseudonymizationContextFromCase({
    client,
    caseRow: null,
    ...(currentDate !== undefined ? { currentDate } : {}),
  });
}
